// MembersTable.jsx
import React, { useContext, useState } from 'react';
import { StatusBadge, RoleBadge, formatDate } from './utilities';
import SglContext from '../../SglContext';

const MembersTable = ({ 
  members, 
  onEditMember, 
  onRemoveMember, 
  onCancelInvite, 
  currentTeam,
  // New props for all teams view
  allTeamsData,
  onInviteMember,
  showAllTeams = false
}) => {
  const Sgl = useContext(SglContext);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPositions, setDropdownPositions] = useState({});
  // const [expandedTeams, setExpandedTeams] = useState(new Set()); // All teams collapsed by default
  // Initialize with all teams expanded by default
  const [expandedTeams, setExpandedTeams] = useState(() => {
    if (showAllTeams && allTeamsData) {
      return new Set(allTeamsData.map(team => team.teamId));
    }
    return new Set();
  });

  const toggleTeam = (teamId) => {
    setExpandedTeams(prev => {
      const newSet = new Set(prev);
      if (newSet.has(teamId)) {
        newSet.delete(teamId);
      } else {
        newSet.add(teamId);
      }
      return newSet;
    });
  };

  // Handle dropdown toggle
  const handleDropdownToggle = (memberId, event) => {
    event.stopPropagation();
    
    if (openDropdown === memberId) {
      setOpenDropdown(null);
      return;
    }

    // Calculate position BEFORE opening
    const trigger = event.target.closest('.actions-menu-container');
    const triggerRect = trigger.getBoundingClientRect();
    const scrollContainer = trigger.closest('[style*="overflow"]') || 
                          trigger.closest('div[style*="overflowX"]') || 
                          trigger.closest('.table') || 
                          document.documentElement;
    const containerRect = scrollContainer.getBoundingClientRect();
    
    const spaceBelow = containerRect.bottom - triggerRect.bottom;
    const shouldPositionAbove = spaceBelow < 100;
    
    // Set position state first
    setDropdownPositions(prev => ({
      ...prev,
      [memberId]: shouldPositionAbove ? 'above' : 'below'
    }));
    
    // Then open dropdown - it will render with correct position
    setOpenDropdown(memberId);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const renderMemberActions = (member, team) => {
    const canManageMembers = team ? team.isOwner : currentTeam?.isOwner;
    const memberOwnRow = member.type == "member" && member?.sglLoginsId == Sgl?.current_user?.sgl_id;

    // Only render actions for owners
    if (!canManageMembers && !memberOwnRow) return null;
    member.canManageMembers = canManageMembers; // Pass down for edit modal context

    return (
      <div className="actions-menu-container">
        <button 
          className="actions-trigger"
          onClick={(e) => handleDropdownToggle(member.sgl_id, e)}
          title="More actions"
        >
          <i className="fas fa-ellipsis-v"></i>
        </button>
        
        {openDropdown === member.sgl_id && (
          <div className={`actions-dropdown open ${dropdownPositions[member.sgl_id] === 'above' ? 'position-above' : ''}`}>
            {member.type === 'invitation' ? (
              <button onClick={() => onCancelInvite(member.sgl_id)} className="danger">
                Cancel
              </button>
            ) : (
              <>
                <button onClick={() => onEditMember(member)}>
                  Edit
                </button>

                {canManageMembers &&
                  <button onClick={() => onRemoveMember(member.sgl_id)} className="danger">
                    Remove
                  </button>
                }
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderMemberInfo = (member) => {
    const displayName = member.type === 'invitation' ? member.email : member.name;
    const email = member.type === 'member' ? member.email : null;
    
    return (
      <div className="member-info">
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '2px' }}>
          {displayName}
        </div>
        {email && (
          <div style={{ fontSize: '13px', color: '#64748b' }}>
            {email}
          </div>
        )}
      </div>
    );
  };

  const renderRoleInfo = (member) => {
    const roleClass = member.role?.name === 'owner' ? 'owner-role' : 
                     member.role?.name === 'manager' ? 'manager-role' : '';
    
    return (
      <div className="role-content">
        <div className={`role-badge ${roleClass}`}>
          {member.role?.name || 'N/A'}
        </div>
        {member.role?.description && (
          <div className="role-description">
            {member.role.description}
          </div>
        )}
      </div>
    );
  };

  const renderDateInfo = (member) => {
    const isInvitation = member.type === 'invitation';
    const dateLabel = isInvitation ? 'INVITED' : 'JOINED';
    const dateValue = formatDate(isInvitation ? member.invitedAt : member.joinedAt);
    
    return (
      <div className="date-content">
        <div className="date-label">{dateLabel}</div>
        <div className="date-value">{dateValue}</div>
      </div>
    );
  };

  const renderTeamSection = (team) => {
    const teamKey = `team-${team.teamId}`;
    const isExpanded = expandedTeams.has(team.teamId);
    
    return (
      <div key={team.teamId} className={`team-card ${isExpanded ? 'expanded' : ''}`} >
        {/* Team Accordion Header */}
        <div 
          className="team-card-header"
          onClick={() => toggleTeam(team.teamId)}
          style={{cursor: 'pointer'}}
        >
          <div className="team-info">
            <h3>
              {team.name}
              <span className={`team-role ${team.isOwner ? 'owner' : 'member'}`}>
                {team.isOwner ? 'Owner' : team.role.name}
              </span>
            </h3>
            <div className="team-meta">
              {team.activeMembers} active members, {team.pendingInvites} pending invites
            </div>
          </div>
          <div className="team-actions">
            {team.isOwner && onInviteMember ? (
              <button
                className="invite-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onInviteMember(team.teamId);
                }}
              >
                <i className="fas fa-user-plus"></i>
                Invite Member
              </button>
            ):(
              <div></div>
            )}
            <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
              <i className="fas fa-chevron-down"></i>
            </span>
          </div>
        </div>

        {/* Members Table - Only show when expanded */}
        {isExpanded && (
          <div className='member-table'>
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th className="display_on_mobile"></th>
                  <th className="display_on_desktop" style={{ width: true ? '35%' : '40%' }}>Name/Email</th>
                  <th className="display_on_desktop" style={{ width: true ? '25%' : '30%' }}>Role</th>
                  <th className="display_on_desktop" style={{ width: true ? '15%' : '15%' }}>Status</th>
                  <th className="display_on_desktop" style={{ width: true ? '15%' : '15%' }}>Date</th>
                  {true && (
                    <th className="display_on_desktop" style={{ width: '10%' }}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {team.members.map((member) => (
                  <tr key={member.sgl_id}>
                    {/* Mobile View */}
                    <td className="display_on_mobile mbl_custom_row">
                      <p className="table_row_p">
                        {true && (
                          <div className="action-buttons trow" style={{marginTop: '10px'}}>
                            {renderMemberActions(member, team)}
                          </div>
                        )}
                        <span className='trow' ><strong>Name:</strong> {member.type === 'invitation' ? member.email : member.name}</span><br/>
                        {member.type === 'member' && (
                          <>
                            <span className='trow'><strong>Email:</strong> {member.email}</span><br/>
                          </>
                        )}
                        <span className='trow'><strong>Role:</strong> 
                          <span className={`role-badge ${member.role?.name === 'owner' ? 'owner-role' : member.role?.name === 'manager' ? 'manager-role' : ''}`} style={{marginLeft: '5px'}}>
                            {member.role?.name || 'N/A'}
                          </span>
                          <br/>
                          {member.role?.description && (
                            <>
                              <div className="role-description">{member.role.description}</div><br/>
                            </>
                          )}
                        </span>
                        <span className='trow'><strong>Status:</strong> <StatusBadge status={member.status} type={member.type} /></span><br/>
                        <span className='trow'><strong>Date:</strong> {
                          member.type === 'invitation' 
                            ? `Invited ${formatDate(member.invitedAt)}`
                            : `Joined ${formatDate(member.joinedAt)}`
                        }</span><br/>
                      </p>
                    </td>

                    {/* Desktop View */}
                    <td className="display_on_desktop">
                      {renderMemberInfo(member)}
                    </td>
                    <td className="display_on_desktop">
                      {renderRoleInfo(member)}
                    </td>
                    <td className="display_on_desktop">
                      <StatusBadge status={member.status} type={member.type} />
                    </td>
                    <td className="display_on_desktop">
                      {renderDateInfo(member)}
                    </td>
                    {true && (
                      <td className="display_on_desktop">
                        <div className="action-buttons">
                          {renderMemberActions(member, team)}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {team.members.length === 0 && (
                  <tr>
                    <td colSpan={true ? "6" : "5"} style={{textAlign: 'center', padding: '20px', color: '#64748b'}}>
                      No team members yet. {team.isOwner ? 'Invite your first member!' : ''}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // If showAllTeams is true, render all teams
  if (showAllTeams && allTeamsData) {
    return (
      <div>
        {allTeamsData.map(team => renderTeamSection(team))}
      </div>
    );
  }

  // Original single team view (Unused but kept for now)
  const canManageMembers = currentTeam?.isOwner;
  return (
    <div>
      <h3>Team Members & Invitations</h3>

      <div className='member-table'>
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th className="display_on_mobile"></th>
              <th className="display_on_desktop" style={{ width: canManageMembers ? '35%' : '40%' }}>Name/Email</th>
              <th className="display_on_desktop" style={{ width: canManageMembers ? '25%' : '30%' }}>Role</th>
              <th className="display_on_desktop" style={{ width: canManageMembers ? '15%' : '15%' }}>Status</th>
              <th className="display_on_desktop" style={{ width: canManageMembers ? '15%' : '15%' }}>Date</th>
              {canManageMembers && (
                <th className="display_on_desktop" style={{ width: '10%' }}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {members && members.map((member) => (
              <tr key={member.sgl_id}>
                {/* Mobile View */}
                <td className="display_on_mobile mbl_custom_row">
                  <p className="table_row_p">
                    {canManageMembers && (
                      <div className="action-buttons trow" style={{marginTop: '10px'}}>
                        {renderMemberActions(member)}
                      </div>
                    )}
                    <span className='trow' ><strong>Name:</strong> {member.type === 'invitation' ? member.email : member.name}</span><br/>
                    {member.type === 'member' && (
                      <>
                        <span className='trow'><strong>Email:</strong> {member.email}</span><br/>
                      </>
                    )}
                    <span className='trow'><strong>Role:</strong> 
                      <span className={`role-badge ${member.role?.name === 'owner' ? 'owner-role' : member.role?.name === 'manager' ? 'manager-role' : ''}`} style={{marginLeft: '5px'}}>
                        {member.role?.name || 'N/A'}
                      </span>
                      <br/>
                      {member.role?.description && (
                        <>
                          <div className="role-description">{member.role.description}</div><br/>
                        </>
                      )}
                    </span>
                    <span className='trow'><strong>Status:</strong> <StatusBadge status={member.status} type={member.type} /></span><br/>
                    <span className='trow'><strong>Date:</strong> {
                      member.type === 'invitation' 
                        ? `Invited ${formatDate(member.invitedAt)}`
                        : `Joined ${formatDate(member.joinedAt)}`
                    }</span><br/>
                  </p>
                </td>

                {/* Desktop View */}
                <td className="display_on_desktop">
                  {renderMemberInfo(member)}
                </td>
                <td className="display_on_desktop">
                  {renderRoleInfo(member)}
                </td>
                <td className="display_on_desktop">
                  <StatusBadge status={member.status} type={member.type} />
                </td>
                <td className="display_on_desktop">
                  {renderDateInfo(member)}
                </td>
                {canManageMembers && (
                  <td className="display_on_desktop">
                    <div className="action-buttons">
                      {renderMemberActions(member)}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {(!members || members.length === 0) && (
              <tr>
                <td colSpan={canManageMembers ? "6" : "5"}>No team members yet. Invite your first member!</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MembersTable;